import { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { safeLocalStorage } from '@/shared/utils/storage'

export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextType {
  theme: ThemeMode
  setTheme: (theme: ThemeMode) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<ThemeMode>(() => {
    // Check for stored preference or system preference
    const stored = safeLocalStorage.getItem('theme') as ThemeMode | null
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
    return 'system'
  })

  const [activeTheme, setActiveTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const updateSystemTheme = () => {
        setActiveTheme(mediaQuery.matches ? 'dark' : 'light')
      }
      
      updateSystemTheme()
      
      mediaQuery.addEventListener('change', updateSystemTheme)
      return () => {
        mediaQuery.removeEventListener('change', updateSystemTheme)
      }
    } else {
      setActiveTheme(theme)
    }
  }, [theme])

  // Create MUI theme based on current theme mode
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: activeTheme,
          primary: {
            main: activeTheme === 'light' ? '#0069d9' : '#90caf9',
            light: activeTheme === 'light' ? '#4dabf5' : '#e3f2fd',
            dark: activeTheme === 'light' ? '#0056b3' : '#42a5f5',
          },
          secondary: {
            main: activeTheme === 'light' ? '#6c757d' : '#ce93d8',
            light: activeTheme === 'light' ? '#868e96' : '#f3e5f5',
            dark: activeTheme === 'light' ? '#495057' : '#ab47bc',
          },
          error: {
            main: activeTheme === 'light' ? '#d32f2f' : '#f44336',
          },
          warning: {
            main: activeTheme === 'light' ? '#a65d00' : '#ffb74d',
          },
          info: {
            main: activeTheme === 'light' ? '#0277bd' : '#64b5f6',
          },
          success: {
            main: activeTheme === 'light' ? '#2e7d32' : '#81c784',
          },
          background: {
            default: activeTheme === 'light' ? '#f8f9fa' : '#121212',
            paper: activeTheme === 'light' ? '#ffffff' : '#1e1e1e',
          },
          text: {
            primary: activeTheme === 'light' ? '#212529' : '#ffffff',
            // #5c6670 keeps ≥5.0:1 on white, grey.50/100/200 and the default
            // background; the former #6c757d measured 4.49:1 on grey.50 (A28).
            secondary: activeTheme === 'light' ? '#5c6670' : '#aaaaaa',
          },
        },
        typography: {
          fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
          ].join(','),
          h1: {
            fontSize: '2.5rem',
            fontWeight: 600,
          },
          h2: {
            fontSize: '2rem',
            fontWeight: 600,
          },
          h3: {
            fontSize: '1.75rem',
            fontWeight: 600,
          },
          h4: {
            fontSize: '1.5rem',
            fontWeight: 600,
          },
          h5: {
            fontSize: '1.25rem',
            fontWeight: 600,
          },
          h6: {
            fontSize: '1rem',
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 500,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: activeTheme === 'light' 
                  ? '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
                  : '0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                boxShadow: 'none',
                borderBottom: `1px solid ${activeTheme === 'light' ? '#e0e0e0' : '#333333'}`,
              },
            },
          },
        },
      }),
    [activeTheme]
  )

  useEffect(() => {
    // Store preference
    safeLocalStorage.setItem('theme', theme)
  }, [theme])

  useEffect(() => {
    // Keep the existing class-based theme for Tailwind compatibility
    const root = window.document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(activeTheme)
  }, [activeTheme])

  const toggleTheme = () => {
    setTheme(activeTheme === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CssBaseline />
          {children}
        </LocalizationProvider>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
